import { faHand } from "@jewlr/pro-light-svg-icons/faHand"
import { faChevronDown } from "@jewlr/pro-regular-svg-icons/faChevronDown"
import { faChevronUp } from "@jewlr/pro-regular-svg-icons/faChevronUp"
import { FocusScope } from "@react-aria/focus"
import PropTypes from "prop-types"
import { useEffect, useState } from "react"
import { connect } from "react-redux"

import {
  ItemAction,
  DetailText,
  ItemTitle,
  ShadowBox,
  RetailPrice,
} from "areas/cart/components"
import { addAddon, removeAddon } from "areas/cart/store/actions"
import Button from "components/ui/button"
import Expand from "components/ui/expand"
import FontAwesomeIcon from "components/ui/font-awesome-icon"
import Image from "components/ui/image"
import Media from "components/ui/media"
import { freeGiftPromoEnabled } from "helpers/holiday"
import { to_currency } from "helpers/number"
import { useNavigation } from "helpers/use-navigation"
import { cn } from "styles/utils"

const GiftButton = ({ className = "", selected, ...props }) => (
  <Button
    className={cn(
      "focus-visible:border-selected ml-10 rounded-sm border-2 border-transparent first:ml-0 focus-visible:outline-none disabled:cursor-not-allowed",
      selected &&
        "border-selected after:content-(--checkmark-icon) after:size-22 after:scale-80 relative after:absolute after:-bottom-9 after:left-1/2 after:block after:-translate-x-1/2",
      className
    )}
    {...props}
  />
)

const FreeGiftItem = ({ gift, handleClick, ...props }) => {
  const keyboardProps = useNavigation({ direction: "horizontal" })

  return (
    <GiftButton
      aria-label={gift.title}
      data-cy={`cart-freegift-desktop-${gift.gift_code}`}
      onClick={handleClick}
      role="radio"
      {...keyboardProps}
      {...props}
    >
      <Image
        alt={gift.title}
        height="68"
        src={gift.image_url}
        title={gift.title}
        width="68"
      />
    </GiftButton>
  )
}

FreeGiftItem.propTypes = {
  gift: PropTypes.object,
  handleClick: PropTypes.func,
}

const NoGiftItem = ({ handleClick, ...props }) => {
  const keyboardProps = useNavigation({ direction: "horizontal" })

  return (
    <GiftButton
      aria-label="No Gift"
      className="bg-[#f8f8f8]"
      data-cy="cart-freegift-desktop-none"
      onClick={handleClick}
      role="radio"
      {...keyboardProps}
      {...props}
    >
      <div className="h-68 w-68 flex flex-col items-center justify-center">
        <FontAwesomeIcon className="text-grey-30 text-20 mb-6" icon={faHand} />
        <p className="text-14 leading-18 tracking-tight">No Thanks</p>
      </div>
    </GiftButton>
  )
}

NoGiftItem.propTypes = {
  handleClick: PropTypes.func,
}

const InternalFreeGifts = ({
  chooseGift,
  freeGifts,
  removeGift,
  selectedFreeGift,
}) => {
  if (!freeGifts?.length) return null

  const [selectedGift, setGift] = useState(selectedFreeGift || freeGifts[0])
  const [showGiftDetails, toggleGiftDetails] = useState(false)

  useEffect(() => {
    if (
      selectedGift &&
      selectedFreeGift &&
      selectedGift.retail_price !== selectedFreeGift.retail_price
    ) {
      setGift(selectedFreeGift)
    }
  }, [selectedFreeGift])

  return (
    <Media greaterThanOrEqual="tablet">
      <ShadowBox>
        <div className="flex gap-10">
          <Image
            alt={selectedGift.title}
            className=""
            data-cy="cart-freegift-selected-img-desktop"
            height="128px"
            src={selectedGift.image_url}
            title={selectedGift.title}
            width="128px"
          />
          <div className="flex-1">
            <ItemTitle
              className="text-green-darker mb-3 font-normal"
              id="cart-free-gift-title-desktop"
            >
              Choose Your FREE GIFT!
            </ItemTitle>
            <ItemTitle className="mb-16 font-semibold leading-tight">
              {selectedGift.title}
            </ItemTitle>
            <ItemAction
              aria-controls="cart-free-gift-details-desktop"
              aria-expanded={showGiftDetails}
              className="text-12 mt-15 leading-20 font-semibold"
              onClick={() => toggleGiftDetails(!showGiftDetails)}
            >
              View Item Details{" "}
              <FontAwesomeIcon
                className="text-8 align-text-bottom"
                icon={showGiftDetails ? faChevronUp : faChevronDown}
              />
            </ItemAction>
            <Expand
              className="mb-12"
              id="cart-free-gift-details-desktop"
              show={showGiftDetails}
            >
              <DetailText>{selectedGift.description}</DetailText>
              {freeGiftPromoEnabled() && (
                <>
                  <br />
                  <DetailText italic>While supplies last.</DetailText>
                </>
              )}
            </Expand>
            <FocusScope>
              <div
                aria-label="Choose your free gift"
                className="mt-10 flex"
                data-cy="cart-freegift-category-desktop"
                role="radiogroup"
              >
                {freeGifts.map((gift, i) => {
                  const selected =
                    (selectedFreeGift?.addon_code ||
                      selectedFreeGift?.gift_code) === gift.gift_code
                  return (
                    <FreeGiftItem
                      aria-selected={selected}
                      gift={gift}
                      handleClick={() => {
                        setGift(gift)
                        chooseGift(gift.gift_code)
                      }}
                      key={`desktop-freegift-${gift.gift_code}`}
                      selected={selected}
                      tabIndex={
                        selected || (!selectedFreeGift && i === 0) ? 0 : -1
                      }
                    />
                  )
                })}
                <NoGiftItem
                  aria-selected={!selectedFreeGift}
                  handleClick={() => removeGift()}
                  selected={!selectedFreeGift}
                  tabIndex={!selectedFreeGift ? 0 : -1}
                />
              </div>
            </FocusScope>
          </div>
          <div className="text-right">
            <p className="text-green-darker text-32 font-light">FREE</p>
            {
              freeGiftPromoEnabled() ? (
                <>
                  {sl.config.free_gift_promo?.retail_value && (
                    <RetailPrice>
                      {to_currency(sl.config.free_gift_promo.retail_value)}{" "}
                      VALUE
                    </RetailPrice>
                  )}
                </>
              ) : null
              // <RetailPrice>
              //   RETAIL VALUE {to_currency(selectedGift.retail_price)}
              // </RetailPrice>
            }
          </div>
        </div>
      </ShadowBox>
    </Media>
  )
}

const mapStateToProps = (state) => {
  return {
    freeGifts: state.cart.free_gift_offers,
    selectedFreeGift: state.cart.addons.find(
      (addon) => addon.addon_type == "GIFT"
    ),
  }
}

const mapDispatchToProps = (dispatch) => {
  return {
    chooseGift: (addon_code) => {
      // Desktop Cart select gift
      dispatch(addAddon(addon_code, "GIFT", "cart_desktop"))
    },
    dispatch,
  }
}

const mergeProps = (stateProps, dispatchProps, ownProps) => {
  const { selectedFreeGift } = stateProps
  const { dispatch } = dispatchProps

  return {
    ...ownProps,
    ...stateProps,
    ...dispatchProps,
    removeGift: () => {
      if (selectedFreeGift) {
        dispatch(removeAddon(selectedFreeGift.id))
      }
    },
  }
}

InternalFreeGifts.propTypes = {
  chooseGift: PropTypes.func,
  freeGifts: PropTypes.array,
  removeGift: PropTypes.func,
  selectedFreeGift: PropTypes.object,
}

GiftButton.propTypes = {
  className: PropTypes.string,
  selected: PropTypes.bool,
}

const DesktopFreeGifts = connect(
  mapStateToProps,
  mapDispatchToProps,
  mergeProps
)(InternalFreeGifts)

export default DesktopFreeGifts
